import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { Dirs, FileSystem } from 'react-native-file-access';

import { LRUCache } from '@/utils/cache/lru-cache';
import { addResizeParamToURL } from '@/utils/cdn-utils';
import { isAndroid } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';
import { SkImage, Skia } from '@shopify/react-native-skia';
import { sha1 } from 'js-sha1';

// Instance of LRUCache to store images in memory with a limit of 50
const memoryCache = new LRUCache<string, SkImage>(50);

/**
 * Hook to cache Skia images in memory and on disk
 *
 * IF the image is already in memory, it will be used directly
 * IF the image is not in memory, it will be downloaded to disk
 * IF the image is not in disk, it will be downloaded from the resized URL
 * IF the resized image fails to download, it will fall back to the original URL
 *
 * Notes:
 * - The memory cache is limited to 50 images to prevent memory issues
 *
 * @param url - The URL of the image to cache
 * @param onLoad - Callback function to call when the image is loaded
 * @param onError - Callback function to call when we fail to load the image (error on resizing, and also on the original image)
 * @returns Instance of SkImage
 *
 */
export function useSkiaImageCache({
    url,
    imageWidth = 256,
    onLoad,
    onError,
}: {
    url?: string;
    imageWidth?: number;
    onLoad?: () => void;
    onError?: (error: unknown) => void;
}): SkImage | undefined {
    const [skiaImage, setSkiaImage] = useState<SkImage | undefined>(() => {
        if (url) {
            const cachedImage = memoryCache.get(url);
            return cachedImage;
        }
    });

    useAndroidRerenderOnAppStateChange();

    const updateImageAndNotify = useCallback(
        (key: string, image: SkImage) => {
            memoryCache.put(key, image);

            setSkiaImage(image);
            onLoad?.();
        },
        [onLoad]
    );

    useEffect(() => {
        if (!url) {
            return;
        }

        // flag to know if the component is unmounted before the image is loaded to avoid setting state
        let cancelled = false;

        const loadImage = async () => {
            const cacheKey = `${url}-${imageWidth}`;

            try {
                const cachedImage = memoryCache.get(cacheKey);
                // check memory cache
                if (cachedImage) {
                    setSkiaImage(cachedImage);
                    onLoad?.();
                    return;
                }
                // check disk cache
                const diskPath = await toDiskPath(cacheKey);
                if (await FileSystem.exists(diskPath)) {
                    const data = await Skia.Data.fromURI(`file://${diskPath}`);
                    const image = Skia.Image.MakeImageFromEncoded(data);
                    if (image && !cancelled) {
                        updateImageAndNotify(cacheKey, image);
                    }
                    return;
                }

                // download to resized image to disk if not cached anywhere
                const resizedUrl = addResizeParamToURL(url, imageWidth);
                try {
                    await downloadToFile(resizedUrl, diskPath);
                } catch (error: unknown) {
                    if (error instanceof DownloadImageError) {
                        // 415 - we do not try to download the original image
                        if (error?.status === 415) {
                            logger.debug(
                                '[Resize]',
                                'Resized download returned 415 — aborting & reporting error',
                                error
                            );
                            if (!cancelled) {
                                onError?.(error);
                            }
                            // 415 - means original image is not supported/corrupted/absent
                            // so, we do not try to download the original image
                            return;
                        }
                    }

                    // any other error, we fall back to original
                    logger.debug('[Resize]', 'Resized download failed, falling back to original', error);
                    await downloadToFile(url, diskPath);
                }

                // load image from disk
                const data = await Skia.Data.fromURI(`file://${diskPath}`);
                const image = Skia.Image.MakeImageFromEncoded(data);

                if (image && !cancelled) {
                    // cache the image in memory
                    updateImageAndNotify(cacheKey, image);
                }
            } catch (error) {
                if (!cancelled) {
                    logger.debug('[Resize]', `Error loading image – ${url}`, error);
                    onError?.(error);
                }
            }
        };

        loadImage();

        return () => {
            cancelled = true;
        };
    }, [url, onError, updateImageAndNotify, imageWidth, onLoad]);

    return skiaImage;
}

/**
 * on Android, we need to force a rerender when the app state changes
 * to ensure that the image is reloaded when the app is brought back to the foreground
 *
 * this issue is skia related and only happens on some Android models
 */
export const useAndroidRerenderOnAppStateChange = () => {
    const [_, setRerender] = useState(0);
    const previousAppState = useRef(AppState.currentState);

    useEffect(() => {
        if (!isAndroid) {
            return;
        }

        const handleChange = (nextAppState: AppState['currentState']) => {
            if (previousAppState.current.match(/inactive|background/) && nextAppState === 'active') {
                setRerender(prev => prev + 1);
            }
            previousAppState.current = nextAppState;
        };

        const subscription = AppState.addEventListener('change', handleChange);

        return () => subscription.remove();
    }, []);
};

/**
 * Custom error that carries the HTTP status code returned by a failed download
 */
class DownloadImageError extends Error {
    constructor(public status: number, message = `Download failed – HTTP ${status}`) {
        super(message);
        this.name = 'DownloadImageError';
    }
}

/**
 * Extracts the file‑extension portion of a URI (ignoring query/hash parts).
 * Falls back to ".png" when no dot is present.
 * @param {string} uri – Full or partial resource URI.
 * @returns {string} The extension (including the leading dot).
 */
const getExtension = (uri: string): string => {
    const uriWithoutParams = uri.split(/[?#]/, 1)[0];
    const dot = uriWithoutParams.lastIndexOf('.');
    return dot === -1 ? '.png' : uriWithoutParams.substring(dot);
};

/**
 * Computes the on‑disk cache path for a URI and guarantees
 * that `Dirs.CacheDir` exists.
 * @param {string} uri – Resource URI.
 * @returns {Promise<string>} Absolute path where the file should be stored.
 */
const toDiskPath = async (uri: string): Promise<string> => {
    const ext = getExtension(uri);
    const hash = sha1(uri);
    const path = `${Dirs.CacheDir}/${hash}${ext}`;

    if (!(await FileSystem.exists(Dirs.CacheDir))) {
        await FileSystem.mkdir(Dirs.CacheDir);
    }
    return path;
};

/**
 * Downloads a remote resource into `toFile`.
 * On failure it removes the partial file and throws {@link DownloadImageError}
 * whose `status` matches the HTTP status code returned.
 * @param {string} fromUrl – URL to fetch.
 * @param {string} toFile – Target file path on disk.
 * @throws {DownloadImageError} When the HTTP request is not successful.
 * @returns {Promise<void>}
 */
const downloadToFile = async (fromUrl: string, toFile: string): Promise<void> => {
    const { ok, status } = await FileSystem.fetch(fromUrl, { path: toFile });

    if (!ok) {
        await FileSystem.unlink(toFile);
        throw new DownloadImageError(status);
    }
};
